import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useOutletContext } from "react-router-dom";

import { SpinnerLoader } from "components/loader/loader";
import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { useClaimsConstants, usePoliciesList } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useMultiPolicyEligibility } from "pages/offer/hooks";
import { useHideSupportChat } from "utils";

import styles from "./ting-offer.module.scss";

export type TingContextState = {
  /** Keystone policy IDs chosen on the property step for Ting enrollment. */
  selectedTingPolicyIds?: string[];
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phone?: string;
  mobile?: ("isMobile" | "isTextOk")[];
  eligibleTingPolicies?: Record<string, unknown>[];
  terms?: string;
  termsAgreed?: boolean;
} | null;

export const TingContext = createContext<{
  state: TingContextState;
  setState: Dispatch<SetStateAction<TingContextState>>;
}>({ state: null, setState: () => null });

export const TingChecker = () => {
  useHideSupportChat();
  const { setFields, state } =
    useMultiStepFormState<TingContextState>(TingContext);
  const { data: claimsConstants } = useClaimsConstants();
  const policyHolderPhone = claimsConstants?.PolicyHolderDetails.phoneNumber;
  const { data: policies } = usePoliciesList();
  const eligibilityData = useOutletContext<KeystoneOfferEligibility>();
  const skipMultiPolicyEligibilityCheck = policies?.length === 1;

  const { additionalPolicyEligibilityData, loading: eligibilityLoading } =
    useMultiPolicyEligibility(
      eligibilityData?.insightPolicyId,
      "ting-offer",
      skipMultiPolicyEligibilityCheck,
    );

  const allEligibility = useMemo<KeystoneOfferEligibility[]>(
    () =>
      eligibilityData
        ? [eligibilityData, ...additionalPolicyEligibilityData]
        : [],
    [eligibilityData, additionalPolicyEligibilityData],
  );

  useEffect(() => {
    if (!policies || allEligibility.length === 0) return;
    const tingEligiblePolicies = policies
      .filter((p) =>
        allEligibility.some((o) => o.insightPolicyId === p.keystonePolicyId),
      )
      .map((p) => ({
        ...p,
        eligibilityDetails: allEligibility.find(
          (o) => o.insightPolicyId === p.keystonePolicyId,
        ),
      }));
    setFields({ eligibleTingPolicies: tingEligiblePolicies });
  }, [policies, setFields, allEligibility]);

  useEffect(() => {
    const terms = eligibilityData?.additionalDetails.find(
      (detail) => detail.assetTypeCode === "PROVIDER_TERMS_OF_USE",
    );
    setFields({
      terms: terms?.assetTextContent,
    });
  }, [eligibilityData?.additionalDetails, setFields]);

  useEffect(() => {
    if (policyHolderPhone && !state?.phone) {
      setFields({ phone: policyHolderPhone ?? "" });
    }
  }, [policyHolderPhone, setFields, state?.phone]);

  if (eligibilityLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <SpinnerLoader />
      </div>
    );
  }

  return <Outlet />;
};

export const TingFlow = () => {
  const [state, setState] = useState<TingContextState>(null);
  return (
    <TingContext.Provider value={{ state, setState }}>
      <TingChecker />
    </TingContext.Provider>
  );
};
